export type RoundingMethod = 'up' | 'down' | 'nearest' | '15sec' | '30sec' | 'none';

export type ContractType = 'AI Ordering' | 'AI Answering' | 'Drive Thru' | 'Medical';

export const CONTRACT_TYPES: ContractType[] = ['AI Ordering', 'AI Answering', 'Drive Thru', 'Medical'];

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
    'AI Ordering': 'AI Ordering',
    'AI Answering': 'AI Answering',
    'Drive Thru': 'Drive Thru',
    'Medical': 'Medical',
};

export type ContractStatus = 'Prospect' | 'Pilot' | 'Client';

export const CONTRACT_STATUSES: ContractStatus[] = ['Prospect', 'Pilot', 'Client'];

export interface BillingConfig {
    aiMinuteRate: number;
    outboundAiMinuteRate: number;
    postTransferMinuteRate: number;
    minutesIncluded: number;
    callRate: number;
    callsIncluded: number;
    roundingMethod: RoundingMethod;
    flatMonthlyFee: number;
    containedCallRate: number;
    nonContainableCallRate: number;
    orderPlacedRate: number;
}

export interface ApplicationBillingOverride {
    applicationId: string;
    config: BillingConfig;
}

export interface OrganizationConfig {
    organizationId: string;
    organizationName?: string;
    contractType?: ContractType;
    contractStatus?: ContractStatus;
    paying?: boolean;
    totalPossibleLocations?: number;
    avgRevenuePerLocation?: number;
    defaultConfig: BillingConfig;
    applicationOverrides: ApplicationBillingOverride[];
    ignoredApplicationIds: string[];
    forceIncludeApplicationIds: string[];
}

export interface MonthlyLocationProjection {
    month: string;  // Format: "YYYY-MM"
    locationCount: number;
}

export interface OrganizationProjection {
    organizationId: string;
    monthlyProjections: MonthlyLocationProjection[];
}

export interface ProjectionsConfig {
    version: number;
    organizationProjections: OrganizationProjection[];
}

export interface RevenueConfig {
    version: number;
    organizations: OrganizationConfig[];
    projections?: ProjectionsConfig;
}

export interface RevenueBreakdownLine {
    count: number;
    revenue: number;
}

export interface ApplicationRevenue {
    applicationId: string;
    applicationName: string;
    totalRevenue: number;
    breakdown: {
        aiMinutes: RevenueBreakdownLine;
        outboundAiMinutes: RevenueBreakdownLine;
        postTransferMinutes: RevenueBreakdownLine;
        calls: RevenueBreakdownLine;
        containedCalls: RevenueBreakdownLine;
        nonContainableCalls: RevenueBreakdownLine;
        ordersPlaced: RevenueBreakdownLine;
        flatFee: number;
    };
    engagementCount: number;
}

export interface OrganizationRevenue {
    organizationId: string;
    organizationName: string;
    totalRevenue: number;
    applications: ApplicationRevenue[];
}

export const DEFAULT_BILLING_CONFIG: BillingConfig = {
    aiMinuteRate: 0,
    outboundAiMinuteRate: 0,
    postTransferMinuteRate: 0,
    minutesIncluded: 0,
    callRate: 0,
    callsIncluded: 0,
    roundingMethod: 'up',
    flatMonthlyFee: 0,
    containedCallRate: 0,
    nonContainableCallRate: 0,
    orderPlacedRate: 0,
};

export const BILLING_FIELD_DESCRIPTIONS: Record<keyof BillingConfig, string> = {
    aiMinuteRate: "Cost per inbound AI minute. Calculated from preTransferDurationMinutes on each engagement - the time the AI spent handling the call before any transfer. Applies to calls without any tag that starts with OUTBOUND_.",
    outboundAiMinuteRate: "Cost per outbound AI minute. Same duration source as AI Minute Rate (preTransferDurationMinutes), but applies only to engagements with any tag that starts with OUTBOUND_.",
    postTransferMinuteRate: "Cost per post-transfer minute. Calculated from postTransferDurationMinutes on each engagement - the time after a transfer occurred.",
    minutesIncluded: "Number of free minutes included per month before per-minute charges apply. Applies to the sum of AI minutes and post-transfer minutes.",
    callRate: "Cost per call. Call count is determined by the number of keys in the serialized sessionSummaries dictionary on each engagement.",
    callsIncluded: "Number of free calls included per month before per-call charges apply.",
    roundingMethod: "How to round minute durations. Options: Round Up (ceil to nearest minute), Round Down (floor), Round to Nearest, 15-second increments, 30-second increments, No Rounding (sum raw seconds, convert to minutes).",
    flatMonthlyFee: "Fixed monthly charge regardless of usage volume.",
    containedCallRate: "Cost per contained call. A call is contained when isContained = true on the engagement.",
    nonContainableCallRate: "Cost per non-containable call. A call is non-containable when isContainable = false on the engagement.",
    orderPlacedRate: "Cost per order placed. Counted when an engagement's sessionTags includes 'ACTION_COMPLETED'.",
};

export const ROUNDING_METHOD_LABELS: Record<RoundingMethod, string> = {
    'up': 'Round Up (nearest minute)',
    'down': 'Round Down (nearest minute)',
    'nearest': 'Round to Nearest Minute',
    '15sec': 'Round to 15-second increments',
    '30sec': 'Round to 30-second increments',
    'none': 'No Rounding (sum raw seconds)',
};

// ==================== PROJECTIONS ====================

export interface ProjectedMonthRevenue {
    month: string;
    locationCount: number;
    averageRevenuePerLocation: number;
    projectedRevenue: number;
}

export interface ProjectedOrganizationRevenue {
    organizationId: string;
    organizationName: string;
    currentLocationCount: number;
    averageRevenuePerLocation: number;
    monthlyProjections: ProjectedMonthRevenue[];
}
